import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const Orders = () => {
  const location = useLocation();
  const phone = location.state?.phone;
  const [orders, setOrders] = useState([]);
  const [timers, setTimers] = useState({});
  const [statuses, setStatuses] = useState({});
  const [latestOrderId, setLatestOrderId] = useState(null);

  useEffect(() => {
  const fetchOrders = async () => {
    try {
      const ordersRef = collection(db, "customers", phone, "orders");
      const snapshot = await getDocs(ordersRef);
      const fetchedOrders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort orders by timestamp (most recent last)
      const sortedOrders = fetchedOrders.sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );

      setOrders(sortedOrders);

      // Set the latest order ID (newest one)
      if (sortedOrders.length > 0) {
        setLatestOrderId(sortedOrders[sortedOrders.length - 1].id);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  if (phone) fetchOrders();
}, [phone]);

  useEffect(() => {
  const interval = setInterval(() => {
    const now = Date.now();
    const newTimers = {};
    const newStatuses = {};

    orders.forEach((order) => {
      const orderTime = new Date(order.timestamp).getTime();
      const timePassed = now - orderTime;
      const timeLeft = Math.max(0, FIVE_MINUTES_MS - timePassed);
      newTimers[order.id] = timeLeft;

      if (timeLeft === 0 && !statuses[order.id]) {
        newStatuses[order.id] = true;

        // ✅ Only alert for the latest order
        if (order.id === latestOrderId) {
          alert(`Order ${order.id} is now completed.`);
        }
      }
    });

    setTimers(newTimers);
    setStatuses((prev) => ({ ...prev, ...newStatuses }));
  }, 1000);

  return () => clearInterval(interval);
}, [orders, statuses, latestOrderId]);

  const formatTimeLeft = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!phone) {
    return (
      <div className="text-center p-10 text-gray-600 text-lg">
        No phone number provided.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-12 px-6 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <h1 className="text-3xl font-extrabold text-gray-800 mb-8 text-center">Your Orders</h1>
      {orders.length === 0 ? (
        <p className="text-center text-gray-500 text-lg">
          No orders found for <strong>{phone}</strong>.
        </p>
      ) : (
        orders.map((order) => (
          <div
            key={order.id}
            className="bg-white/60 backdrop-blur-md border border-gray-200 rounded-xl p-6 mb-8 shadow-lg transition hover:shadow-2xl"
          >
            <div className="mb-4">
              <p className="text-sm text-gray-500">Order ID:</p>
              <p className="font-bold text-lg text-gray-800">{order.id}</p>
              <p className="text-sm text-gray-600">
                Placed at: {new Date(order.timestamp).toLocaleTimeString()}
              </p>
              <p className="text-sm text-blue-600 font-medium">
                Time left: {formatTimeLeft(timers[order.id] || 0)}
              </p>
              <p
                className={`mt-2 font-semibold ${
                  statuses[order.id]
                    ? "text-green-600"
                    : order.type === "dinein"
                    ? "text-orange-600"
                    : "text-amber-600"
                }`}
              >
                Status:{" "}
                {statuses[order.id]
                  ? order.type === "dinein"
                    ? "Order Delivered"
                    : "Out for Delivery"
                  : order.type === "dinein"
                  ? "Preparing your order"
                  : "Packing in progress"}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-inner p-4 mb-4">
              <h3 className="text-md font-bold text-gray-700 mb-2">Items:</h3>
              <ul className="divide-y divide-gray-200">
                {order.items.map((item, index) => (
                  <li
                    key={index}
                    className="flex justify-between items-center py-2 text-gray-800"
                  >
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity || 1}
                      </p>
                    </div>
                    <p className="font-semibold">
                      ₹{(item.price * (item.quantity || 1)).toFixed(2)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-between items-center">
              <p className="text-gray-700 font-semibold">
                {order.type === "dinein" ? (
                  <>Table No: <span className="text-gray-900">{order.tableNumber}</span></>
                ) : (
                  "Takeout Order"
                )}
              </p>
              <p className="text-xl font-bold text-gray-800">
                Total: ₹{order.total.toFixed(2)}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Orders;
